import { useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useSearchParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, AUTH_ENABLED } from '../lib/auth';
import { Calculator } from '../components/calculator/Calculator';
import type { ProfileContext } from '../components/calculator/Calculator';
import { useAnalysisDetail } from '../hooks/useAnalyses';
import { useProfileDetail } from '../hooks/useProfiles';
import { usePropertyDetail } from '../hooks/useProperties';
import { useOffers } from '../hooks/useOffers';
import { calculateAll, getDefaultInputs } from '../lib/calculator';
import { computePriceLevels } from '../lib/price-levels';
import { apiFetch } from '../lib/api';
import { fmtDollarInt } from '../lib/format';
import type { Analysis, CalculatorInputs, CalculatorResults, CreditScoreRange } from '../lib/types';

// Lenders use the lower of the two borrowers' middle credit scores
function lowerCreditScore(a: CreditScoreRange, b: CreditScoreRange): CreditScoreRange {
  return (parseInt(a) <= parseInt(b) ? a : b) as CreditScoreRange;
}


function ProfileBanner({ profile }: { profile: NonNullable<ReturnType<typeof useProfileDetail>['profile']> }) {
  return (
    <div className="profile-banner" style={{ background: 'var(--card-bg)' }}>
      <span style={{ fontWeight: 700, color: 'var(--primary)', marginRight: '0.25rem' }}>
        👤 {profile.name}
      </span>
      <span style={{ color: 'var(--text-muted)' }}>·</span>
      <span>
        <span style={{ color: 'var(--text-muted)' }}>Cash: </span>
        <strong>{fmtDollarInt(profile.cashOnHand)}</strong>
      </span>
      <span>
        <span style={{ color: 'var(--text-muted)' }}>Income: </span>
        <strong>{fmtDollarInt(profile.grossAnnualIncome)}/yr</strong>
      </span>
      <span>
        <span style={{ color: 'var(--text-muted)' }}>Credit: </span>
        <strong>{profile.creditScoreRange}+</strong>
      </span>
      {profile.monthlyDebts > 0 && (
        <span>
          <span style={{ color: 'var(--text-muted)' }}>Debts: </span>
          <strong>{fmtDollarInt(profile.monthlyDebts)}/mo</strong>
        </span>
      )}
      {profile.use401k && (
        <span style={{
          background: 'rgba(59,130,246,0.1)',
          color: '#3b82f6',
          padding: '2px 7px',
          borderRadius: '4px',
          fontWeight: 600,
        }}>
          401k {fmtDollarInt(profile.loan401kAmount ?? 25000)}
        </span>
      )}
    </div>
  );
}

function AnalysisContent() {
  const { profileId, propertyId, analysisId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken } = useAuth();

  // "new" mode: user arrived from the price-level spread, not from a specific saved analysis.
  // We start with fresh inputs and only create an analysis record when they save or plan an offer.
  const isNewMode = analysisId === 'new';

  // Base inputs passed via router state when navigating from the property page.
  // These capture the current property/profile configuration so the calculator opens
  // with the right rate, term, income, etc. without us having to re-fetch all that here.
  const passedBaseInputs = (location.state as { baseInputs?: CalculatorInputs } | null)?.baseInputs;

  const { profile } = useProfileDetail(profileId, getToken);
  const { property } = usePropertyDetail(profileId, propertyId, getToken);
  // Only fetch the existing analysis in "existing" mode — in new mode there's nothing to load.
  const { analysis, loading, error } = useAnalysisDetail(
    propertyId,
    isNewMode ? undefined : analysisId,
    getToken
  );
  const { offers, createOffer } = useOffers(propertyId, getToken);

  const [regenerating, setRegenerating] = useState(false);
  const [planningOffer, setPlanningOffer] = useState(false);

  // Tracks whether we just auto-saved from new mode and navigated to a real ID.
  // While the fetch for the newly-created analysis is in flight, we don't want to
  // show a loading screen — the Calculator already has the right state and unmounting
  // it would flash white and lose the "✓ Auto-saved" toast.
  const justSavedFromNewRef = useRef(false);

  // If a ?price= query param is provided, override the home price
  const overridePrice = searchParams.get('price') ? Number(searchParams.get('price')) : null;

  // If a ?returnToOffer= param is present, we were launched from the offer plan page.
  // After saving, navigate back to the offer with the new analysis ID for auto-apply.
  const returnToOfferId = searchParams.get('returnToOffer');

  // Live calculator state — updated on every input change via onStateChange.
  // Used in new mode so "Plan Offer" can save + create the offer in one click
  // without waiting for the 3s auto-save debounce.
  const liveStateRef = useRef<{ inputs: CalculatorInputs; results: CalculatorResults } | null>(null);
  const handleStateChange = useCallback((inputs: CalculatorInputs, results: CalculatorResults) => {
    liveStateRef.current = { inputs, results };
  }, []);

  // --- Inputs for the calculator ---

  // Existing mode: use the saved analysis inputs as-is — these are the user's configuration.
  // Only the ?price= override is applied (for viewing a specific price point within this analysis).
  // Profile defaults like defaultDownPaymentPct are NOT applied here — they're the user's
  // saved choices. Profile defaults are only used when creating a *new* analysis (newModeInputs).
  const existingModeInputs = useMemo((): Partial<CalculatorInputs> | undefined => {
    if (!analysis) return undefined;

    const inputs = { ...analysis.inputs };

    if (overridePrice && overridePrice > 0) {
      inputs.homePrice = overridePrice;
      inputs.downPaymentAmt = Math.round(overridePrice * (inputs.downPaymentPct / 100));
    }

    return inputs;
  }, [analysis, overridePrice]);

  // New mode: use the base inputs passed from the property page, with the override price applied.
  // Falls back to property/profile defaults if no state was passed (e.g. direct deep-link).
  const newModeInputs = useMemo((): Partial<CalculatorInputs> | undefined => {
    if (!isNewMode) return undefined;

    if (passedBaseInputs) {
      const inputs = { ...passedBaseInputs };
      if (overridePrice && overridePrice > 0) {
        inputs.homePrice = overridePrice;
        inputs.downPaymentAmt = Math.round(overridePrice * (inputs.downPaymentPct / 100));
      }
      return inputs;
    }

    // Fallback: build from property + profile (no cached state available)
    if (!property) return undefined;
    const inputs: CalculatorInputs = { ...getDefaultInputs() };
    inputs.homePrice = overridePrice && overridePrice > 0 ? overridePrice : property.askingPrice;
    inputs.downPaymentAmt = Math.round(inputs.homePrice * (inputs.downPaymentPct / 100));
    inputs.propertyAddress = property.address;
    inputs.propertyCity = property.city;
    inputs.propertyZip = property.zip;
    if (property.county) inputs.mnCounty = property.county;
    if (profile) {
      const co = profile.coBorrower;
      inputs.grossAnnualIncome = profile.grossAnnualIncome + (co?.grossAnnualIncome ?? 0);
      inputs.creditScore = co
        ? lowerCreditScore(profile.creditScoreRange, co.creditScoreRange)
        : profile.creditScoreRange;
      inputs.monthlyOtherDebt = profile.monthlyDebts + (co?.monthlyDebts ?? 0);
    }
    if (profile?.defaultDownPaymentPct !== undefined) {
      inputs.downPaymentPct = profile.defaultDownPaymentPct;
      inputs.downPaymentAmt = Math.round(inputs.homePrice * (profile.defaultDownPaymentPct / 100));
    }
    return inputs;
  }, [isNewMode, passedBaseInputs, overridePrice, property, profile]);

  const initialInputs = isNewMode ? newModeInputs : existingModeInputs;

  const profileContext = useMemo((): ProfileContext | undefined => {
    if (!profile) return undefined;
    return {
      name: profile.name,
      cashOnHand: profile.cashOnHand,
      grossAnnualIncome: profile.grossAnnualIncome,
      creditScoreRange: profile.creditScoreRange,
      use401k: profile.use401k,
      loan401kAmount: profile.loan401kAmount,
    };
  }, [profile]);

  // Stale detection — only relevant in existing mode
  const staleReasons = useMemo(() => {
    if (isNewMode || !analysis) return [];
    const reasons: string[] = [];
    if (property?.updatedAt && property.updatedAt > analysis.createdAt) {
      reasons.push('property');
    }
    if (profile?.updatedAt && profile.updatedAt > analysis.createdAt) {
      reasons.push('profile');
    }
    return reasons;
  }, [isNewMode, analysis, property, profile]);

  const isStale = staleReasons.length > 0;

  // Rebuild the analysis from current property + profile data, keeping user-configured
  // calculator settings (rate, term, loan type, closing costs, etc.) from the saved inputs.
  const handleRegenerate = useCallback(async () => {
    if (!analysis || !property || !profileId || !propertyId) return;
    setRegenerating(true);
    try {
      const co = profile?.coBorrower;
      const combinedIncome = profile
        ? profile.grossAnnualIncome + (co?.grossAnnualIncome ?? 0)
        : analysis.inputs.grossAnnualIncome;
      const combinedDebts = profile
        ? profile.monthlyDebts + (co?.monthlyDebts ?? 0)
        : analysis.inputs.monthlyOtherDebt;
      const effectiveCredit = profile
        ? (co ? lowerCreditScore(profile.creditScoreRange, co.creditScoreRange) : profile.creditScoreRange)
        : analysis.inputs.creditScore;
      const dpPct = profile?.defaultDownPaymentPct ?? analysis.inputs.downPaymentPct;

      const freshInputs: CalculatorInputs = {
        ...analysis.inputs,
        homePrice: property.askingPrice,
        downPaymentPct: dpPct,
        downPaymentAmt: Math.round(property.askingPrice * (dpPct / 100)),
        propertyAddress: property.address,
        propertyCity: property.city,
        propertyZip: property.zip,
        grossAnnualIncome: combinedIncome,
        creditScore: effectiveCredit,
        monthlyOtherDebt: combinedDebts,
        monthlyCarPayment: 0,
        monthlyStudentLoans: 0,
        monthlyCreditCards: 0,
        use401k: profile?.use401k ?? analysis.inputs.use401k,
        loan401kAmount: profile?.loan401kAmount ?? analysis.inputs.loan401kAmount,
        loan401kRate: profile?.loan401kRate ?? analysis.inputs.loan401kRate,
        loan401kTerm: profile?.loan401kTerm ?? analysis.inputs.loan401kTerm,
      };

      const freshResults = calculateAll(freshInputs);
      const freshPriceLevels = computePriceLevels(property.askingPrice, freshInputs);

      const newAnalysis = await apiFetch<Analysis>(
        `/properties/${propertyId}/analyses`,
        {
          method: 'POST',
          body: JSON.stringify({
            askingPrice: property.askingPrice,
            inputs: freshInputs,
            results: freshResults,
            priceLevels: freshPriceLevels,
            profileId,
          }),
        },
        getToken
      );

      const isLinkedToOffer = offers.some(o => o.analysisId === analysis.id);
      if (!isLinkedToOffer) {
        await apiFetch(
          `/properties/${propertyId}/analyses/${analysis.id}`,
          { method: 'DELETE' },
          getToken
        );
      }

      navigate(`/profiles/${profileId}/properties/${propertyId}/analyses/${newAnalysis.id}`);
    } catch (err) {
      console.error('Failed to regenerate analysis:', err);
      setRegenerating(false);
    }
  }, [analysis, property, profile, profileId, propertyId, offers, getToken, navigate]);

  // Save the current calculator state as a new analysis snapshot.
  // When launched from the offer plan page (?returnToOffer=...), navigate back to the offer
  // so it can auto-apply the new analysis. Otherwise, replace the current URL with the new one.
  const handleSaveAnalysis = useCallback(async (
    inputs: CalculatorInputs,
    results: CalculatorResults
  ) => {
    if (!profileId || !propertyId || regenerating) return;
    const priceLevels = computePriceLevels(inputs.homePrice, inputs);
    const newAnalysis = await apiFetch<Analysis>(
      `/properties/${propertyId}/analyses`,
      {
        method: 'POST',
        body: JSON.stringify({
          askingPrice: inputs.homePrice,
          inputs,
          results,
          priceLevels,
          profileId,
        }),
      },
      getToken
    );
    if (returnToOfferId) {
      navigate(
        `/profiles/${profileId}/properties/${propertyId}/offers/${returnToOfferId}?applyAnalysis=${newAnalysis.id}`
      );
    } else {
      // Mark that we're transitioning from new mode so the loading guard doesn't
      // unmount the Calculator while the fetch for the newly-saved analysis is in flight.
      if (isNewMode) justSavedFromNewRef.current = true;
      navigate(
        `/profiles/${profileId}/properties/${propertyId}/analyses/${newAnalysis.id}`,
        { replace: true }
      );
    }
  }, [isNewMode, profileId, propertyId, returnToOfferId, getToken, navigate, regenerating]);

  // --- Plan Offer ---

  // Save the current live state as an analysis (if needed), then create an offer.
  // In new mode there's no pre-existing analysis, so we always save first.
  // In existing mode, we use the already-saved analysis ID.
  const handlePlanOffer = useCallback(async () => {
    if (!profileId || !propertyId) return;
    setPlanningOffer(true);

    try {
      let analysisIdForOffer: string;

      if (isNewMode) {
        // Capture the current live state. liveStateRef is populated by onStateChange on
        // every render, so it's always current (even before the 3s auto-save fires).
        const live = liveStateRef.current;
        if (!live) return;

        const priceLevels = computePriceLevels(live.inputs.homePrice, live.inputs);
        const saved = await apiFetch<Analysis>(
          `/properties/${propertyId}/analyses`,
          {
            method: 'POST',
            body: JSON.stringify({
              askingPrice: live.inputs.homePrice,
              inputs: live.inputs,
              results: live.results,
              priceLevels,
              profileId,
            }),
          },
          getToken
        );
        analysisIdForOffer = saved.id;
      } else {
        if (!analysis) return;
        analysisIdForOffer = analysis.id;
      }

      const offerPrice = overridePrice && overridePrice > 0
        ? overridePrice
        : (isNewMode ? liveStateRef.current!.inputs.homePrice : analysis!.askingPrice);

      const baseInputs = isNewMode ? liveStateRef.current!.inputs : analysis!.inputs;
      const inputs: CalculatorInputs = {
        ...baseInputs,
        homePrice: offerPrice,
        downPaymentAmt: Math.round(offerPrice * (baseInputs.downPaymentPct / 100)),
      };
      const results = calculateAll(inputs);

      const closingDate = new Date();
      closingDate.setDate(closingDate.getDate() + 45);

      const offer = await createOffer({
        analysisId: analysisIdForOffer,
        offerPrice,
        profileId,
        inputs,
        results,
        earnestMoney: Math.round(offerPrice * 0.01),
        closingDate: closingDate.toISOString().slice(0, 10),
        contingencies: { inspection: true, appraisal: true, financing: true, saleDependency: false },
        notes: '',
        status: 'draft',
      });
      navigate(`/profiles/${profileId}/properties/${propertyId}/offers/${offer.id}`);
    } catch (err: any) {
      alert(`Failed to create offer: ${err.message}`);
      setPlanningOffer(false);
    }
  }, [isNewMode, analysis, profileId, propertyId, overridePrice, createOffer, navigate, getToken]);

  // --- Loading / error states ---

  // Clear the just-saved-from-new flag once the analysis fetch completes.
  if (justSavedFromNewRef.current && analysis) {
    justSavedFromNewRef.current = false;
  }

  // In new mode: show the calculator as soon as we have enough data to compute initial inputs.
  // In existing mode: wait for the analysis fetch — UNLESS we just auto-saved from new mode,
  // in which case the Calculator already has the right state and we don't want to unmount it.
  if (!isNewMode && loading && !analysis && !justSavedFromNewRef.current) {
    return (
      <div className="page-container">
        <h2 className="page-title">Analysis Detail</h2>
        <div className="empty-state"><div className="empty-state-text">Loading...</div></div>
      </div>
    );
  }

  if (!isNewMode && !justSavedFromNewRef.current && (error || !analysis)) {
    return (
      <div className="page-container">
        <h2 className="page-title">Analysis Detail</h2>
        <div className="note-box" style={{ borderColor: 'var(--red)', background: 'rgba(239,68,68,0.1)' }}>
          {error || 'Analysis not found'}
        </div>
        <Link
          to={`/profiles/${profileId}/properties/${propertyId}`}
          className="btn btn-secondary"
          style={{ marginTop: '1rem', display: 'inline-block' }}
        >
          ← Back to Property
        </Link>
      </div>
    );
  }

  // In new mode, wait until we have initial inputs (property must be loaded)
  if (isNewMode && !initialInputs) {
    return (
      <div className="page-container">
        <h2 className="page-title">Calculator</h2>
        <div className="empty-state"><div className="empty-state-text">Loading...</div></div>
      </div>
    );
  }

  // --- Render ---

  // During the just-saved-from-new transition, analysis may still be null while the
  // fetch is in flight. For rendering purposes, treat this like we're still in new mode
  // (the Calculator is still showing the same state, just the URL has changed).
  const showAsNewMode = isNewMode || justSavedFromNewRef.current;

  const viewedPrice = overridePrice || (showAsNewMode ? initialInputs?.homePrice : analysis?.askingPrice) || 0;

  const staleBannerText = staleReasons.includes('property') && staleReasons.includes('profile')
    ? 'The property details and buyer profile have been updated since this analysis was saved.'
    : staleReasons.includes('property')
    ? 'The property details have been updated since this analysis was saved.'
    : 'The buyer profile has been updated since this analysis was saved.';

  // Existing-mode offer button: show "View Offer Plan" only when an offer at this exact
  // price is already linked to this analysis. Otherwise always show "Plan Offer".
  const existingOffer = !showAsNewMode && !returnToOfferId
    ? offers.find(o => o.analysisId === analysisId && o.offerPrice === viewedPrice)
    : undefined;

  return (
    <div>
      <div className="page-container" style={{ paddingBottom: '1rem' }}>
        <Link
          to={`/profiles/${profileId}/properties/${propertyId}`}
          style={{ color: 'var(--primary)', fontSize: '0.85rem', textDecoration: 'none' }}
        >
          ← Back to {profile?.name || 'Property'}
        </Link>
        <h2 className="page-title" style={{ marginTop: '0.75rem', marginBottom: '0.15rem' }}>
          {showAsNewMode ? 'Calculator' : `Analysis — ${fmtDollarInt(overridePrice || analysis!.askingPrice)}`}
        </h2>
        <p className="page-subtitle" style={{ marginBottom: 0 }}>
          {showAsNewMode
            ? (overridePrice
                ? `Exploring at ${fmtDollarInt(overridePrice)}`
                : 'New analysis — adjust inputs and save when ready')
            : (overridePrice
                ? `Viewing at ${fmtDollarInt(overridePrice)} (asking: ${fmtDollarInt(analysis!.askingPrice)})`
                : `Saved ${new Date(analysis!.createdAt).toLocaleString()}`)}
        </p>
        {returnToOfferId && (
          <div style={{ fontSize: '0.82rem', color: 'var(--primary)', marginTop: '0.4rem' }}>
            ← Editing for offer plan — adjust inputs, then save when ready to return
          </div>
        )}
        {profile && <ProfileBanner profile={profile} />}

        {/* Plan Offer / View Offer button */}
        {!returnToOfferId && (
          existingOffer ? (
            <div style={{ marginTop: '0.75rem' }}>
              <Link
                to={`/profiles/${profileId}/properties/${propertyId}/offers/${existingOffer.id}`}
                className="btn btn-sm btn-secondary"
              >
                View Offer Plan →
              </Link>
            </div>
          ) : (
            <div style={{ marginTop: '0.75rem' }}>
              <button
                className="btn btn-sm btn-primary"
                onClick={handlePlanOffer}
                disabled={planningOffer}
              >
                {planningOffer ? 'Creating…' : `Plan Offer at ${fmtDollarInt(viewedPrice)} →`}
              </button>
            </div>
          )
        )}

        {/* Stale analysis warning — existing mode only */}
        {isStale && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.65rem 0.9rem',
            background: 'rgba(234,179,8,0.1)',
            border: '1px solid rgba(234,179,8,0.5)',
            borderRadius: '8px',
            marginTop: '0.75rem',
            fontSize: '0.85rem',
          }}>
            <span style={{ flexShrink: 0 }}>⚠️</span>
            <span style={{ flex: 1 }}>
              <strong>This analysis may be outdated.</strong>{' '}
              {staleBannerText}
            </span>
            <button
              onClick={handleRegenerate}
              disabled={regenerating}
              className="btn btn-primary"
              style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem', whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {regenerating ? 'Regenerating…' : 'Regenerate'}
            </button>
          </div>
        )}
      </div>
      <Calculator
        initialInputs={initialInputs}
        profileContext={profileContext}
        onSaveAnalysis={handleSaveAnalysis}
        onStateChange={handleStateChange}
      />
    </div>
  );
}

export function AnalysisPage() {
  if (!AUTH_ENABLED) {
    return (
      <div>
        <div className="page-container" style={{ paddingBottom: 0 }}>
          <h2 className="page-title">Analysis Detail</h2>
          <p className="page-subtitle">Sign in to view saved analyses.</p>
        </div>
        <Calculator />
      </div>
    );
  }

  return <AnalysisContent />;
}
